import { cva, type VariantProps } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { cn } from '../lib/cn'

const badgeVariants = cva(
  'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
  {
    variants: {
      variant: {
        default: 'bg-surface-workspace text-text-secondary',
        brand: 'bg-brand-primary/10 text-brand-primary',
        success: 'bg-status-done/15 text-status-done',
        warning: 'bg-status-working/15 text-status-working',
        danger: 'bg-status-stuck/15 text-status-stuck',
      },
    },
    defaultVariants: { variant: 'default' },
  },
)

export type BadgeProps = HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof badgeVariants>

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />
}

export type StatusVariant =
  | 'done'
  | 'working'
  | 'stuck'
  | 'info'
  | 'onhold'
  | 'purple'
  | 'orange'

const statusBg: Record<StatusVariant, string> = {
  done: 'bg-status-done',
  working: 'bg-status-working',
  stuck: 'bg-status-stuck',
  info: 'bg-status-info',
  onhold: 'bg-status-onhold text-text-primary',
  purple: 'bg-status-purple',
  orange: 'bg-status-orange',
}

export type StatusPillProps = HTMLAttributes<HTMLSpanElement> & {
  status: StatusVariant
}

/** Colored status chip — color carries meaning first (Monday.com style). */
export function StatusPill({ status, className, children, ...props }: StatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold text-white',
        statusBg[status],
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}
